import { beforeEach, describe, expect, it, vi } from 'vitest';
import { sdd_collections_orchestrator as sddCollectionsOrchestrator } from '@repo/domain-types/transfers';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import {
  signMandatesInvitation,
  type ApiCreatePublicMandateRequest,
} from './sign-mandates-invitation';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  apiBaseURL: '/api',
}));

const ApiClientKind = sddCollectionsOrchestrator.mandates.firstparty.v1.ApiClientKind;

describe(signMandatesInvitation, () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const URL = '/api/v1/direct_debit_collection_invitation_links/shared';

  beforeEach(() => {
    vi.clearAllMocks();
  });

  const baseIndividualParams: ApiCreatePublicMandateRequest = {
    invitationLinkId: 'invitation-123',
    kind: ApiClientKind.CLIENT_KIND_INDIVIDUAL,
    firstName: 'Jean',
    lastName: 'Dupont',
    email: 'jean.dupont@example.com',
    address: '18 rue de Navarin',
    city: 'Paris',
    zipCode: '75009',
    countryCode: 'FR',
    iban: 'FR1420041010050500013M02606',
  };

  const baseCompanyParams: ApiCreatePublicMandateRequest = {
    invitationLinkId: 'invitation-123',
    kind: ApiClientKind.CLIENT_KIND_COMPANY,
    companyName: 'ACME Corp',
    email: 'contact@acme.com',
    address: '42 Avenue des Champs-Élysées',
    city: 'Paris',
    zipCode: '75008',
    countryCode: 'FR',
    iban: 'FR7630001007941234567890185',
  };

  it('signs mandate for individual client successfully', async () => {
    mockQontoClient.mockResolvedValue({});

    const result = await signMandatesInvitation(baseIndividualParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/invitation-123/mandates`, {
      method: 'POST',
      body: {
        invitationLinkId: 'invitation-123',
        kind: 'CLIENT_KIND_INDIVIDUAL',
        firstName: 'Jean',
        lastName: 'Dupont',
        email: 'jean.dupont@example.com',
        address: '18 rue de Navarin',
        city: 'Paris',
        zipCode: '75009',
        countryCode: 'FR',
        iban: 'FR1420041010050500013M02606',
      },
    });
    expect(result).toStrictEqual({});
  });

  it('signs mandate for company client successfully', async () => {
    mockQontoClient.mockResolvedValue({});

    const result = await signMandatesInvitation(baseCompanyParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/invitation-123/mandates`, {
      method: 'POST',
      body: {
        invitationLinkId: 'invitation-123',
        kind: 'CLIENT_KIND_COMPANY',
        companyName: 'ACME Corp',
        email: 'contact@acme.com',
        address: '42 Avenue des Champs-Élysées',
        city: 'Paris',
        zipCode: '75008',
        countryCode: 'FR',
        iban: 'FR7630001007941234567890185',
      },
    });
    expect(result).toStrictEqual({});
  });

  it('signs mandate for freelancer client successfully', async () => {
    mockQontoClient.mockResolvedValue({});

    const freelancerParams: ApiCreatePublicMandateRequest = {
      invitationLinkId: 'invitation-456',
      kind: ApiClientKind.CLIENT_KIND_FREELANCER,
      firstName: 'Marie',
      lastName: 'Martin',
      email: 'marie.martin@freelance.com',
      address: '10 Boulevard Saint-Michel',
      city: 'Lyon',
      zipCode: '69001',
      countryCode: 'FR',
      iban: 'FR1420041010050500013M02606',
    };

    await signMandatesInvitation(freelancerParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/invitation-456/mandates`, {
      method: 'POST',
      body: {
        invitationLinkId: 'invitation-456',
        kind: 'CLIENT_KIND_FREELANCER',
        firstName: 'Marie',
        lastName: 'Martin',
        email: 'marie.martin@freelance.com',
        address: '10 Boulevard Saint-Michel',
        city: 'Lyon',
        zipCode: '69001',
        countryCode: 'FR',
        iban: 'FR1420041010050500013M02606',
      },
    });
  });

  it('signs mandate with province for Italy', async () => {
    mockQontoClient.mockResolvedValue({});

    const italianParams: ApiCreatePublicMandateRequest = {
      ...baseIndividualParams,
      countryCode: 'IT',
      province: 'MI',
    };

    await signMandatesInvitation(italianParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/invitation-123/mandates`, {
      method: 'POST',
      body: {
        invitationLinkId: 'invitation-123',
        kind: 'CLIENT_KIND_INDIVIDUAL',
        firstName: 'Jean',
        lastName: 'Dupont',
        email: 'jean.dupont@example.com',
        address: '18 rue de Navarin',
        city: 'Paris',
        zipCode: '75009',
        countryCode: 'IT',
        iban: 'FR1420041010050500013M02606',
        province: 'MI',
      },
    });
  });

  it('signs mandate with only required fields for individual', async () => {
    mockQontoClient.mockResolvedValue({});

    const minimalIndividualParams: ApiCreatePublicMandateRequest = {
      invitationLinkId: 'invitation-789',
      kind: ApiClientKind.CLIENT_KIND_INDIVIDUAL,
      email: 'minimal@example.com',
      address: 'Rue du Test',
      city: 'TestVille',
      zipCode: '12345',
      countryCode: 'FR',
      iban: 'FR1420041010050500013M02606',
    };

    await signMandatesInvitation(minimalIndividualParams);

    expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/invitation-789/mandates`, {
      method: 'POST',
      body: {
        invitationLinkId: 'invitation-789',
        kind: 'CLIENT_KIND_INDIVIDUAL',
        email: 'minimal@example.com',
        address: 'Rue du Test',
        city: 'TestVille',
        zipCode: '12345',
        countryCode: 'FR',
        iban: 'FR1420041010050500013M02606',
      },
    });
  });

  it('constructs correct URL with invitation link ID', async () => {
    mockQontoClient.mockResolvedValue({});

    const customInvitationId = 'custom-invitation-id-123';
    const params: ApiCreatePublicMandateRequest = {
      ...baseIndividualParams,
      invitationLinkId: customInvitationId,
    };

    await signMandatesInvitation(params);

    expect(mockQontoClient).toHaveBeenCalledWith(
      `${URL}/${customInvitationId}/mandates`,
      expect.objectContaining({
        method: 'POST',
      })
    );
  });

  describe('error handling', () => {
    it('throws error for invalid IBAN', async () => {
      const apiError = {
        status: 422,
        data: {
          errors: [
            {
              code: 'BAD_REQUEST_ERROR_CODE_FORMAT_ERROR',
              source: { pointer: '/iban' },
              detail: 'Invalid IBAN format',
            },
          ],
        },
      };

      mockQontoClient.mockRejectedValue(apiError);

      await expect(signMandatesInvitation(baseIndividualParams)).rejects.toStrictEqual(apiError);
    });

    it('throws error for missing required field', async () => {
      const apiError = {
        status: 422,
        data: {
          errors: [
            {
              code: 'BAD_REQUEST_ERROR_CODE_MISSING_REQUIRED_FIELD',
              source: { pointer: '/email' },
              detail: 'Email is required',
            },
          ],
        },
      };

      mockQontoClient.mockRejectedValue(apiError);

      await expect(signMandatesInvitation(baseIndividualParams)).rejects.toStrictEqual(apiError);
    });

    it('throws error when invitation link is not found', async () => {
      const apiError = {
        status: 404,
        data: {
          errors: [
            {
              code: 'NOT_FOUND',
              detail: 'Invitation link not found',
            },
          ],
        },
      };

      mockQontoClient.mockRejectedValue(apiError);

      await expect(signMandatesInvitation(baseIndividualParams)).rejects.toStrictEqual(apiError);
    });

    it('throws error when invitation link is expired', async () => {
      const apiError = {
        status: 410,
        data: {
          errors: [
            {
              code: 'GONE',
              detail: 'Invitation link has expired',
            },
          ],
        },
      };

      mockQontoClient.mockRejectedValue(apiError);

      await expect(signMandatesInvitation(baseIndividualParams)).rejects.toStrictEqual(apiError);
    });
  });
});
